<template>
  <v-container fluid :style="'height:' + (height - 50) + 'px; overflow: auto;'">
    <v-card flat>
      <v-card flat class="text-left pa-3">
        <v-card-title><h1 class="h1">Group Settings</h1></v-card-title>
        <v-card-actions class="pb-0">
          <v-divider></v-divider>
        </v-card-actions>
        <v-alert
          text="Group Admins can customise their Group within this module. Please note that any changes made will affect all users within this Group."
          type="warning"
          variant="tonal"
        ></v-alert>
      </v-card>
      <v-card v-if="loading" flat :style="'height:' + (height - 258) + 'px;'">
        <div class="progress">
          <v-progress-circular
            :size="190"
            :width="2"
            color="#51627C"
            indeterminate
          >
            Loading settings
          </v-progress-circular>
        </div>
      </v-card>
      <v-card v-else flat style="border: solid #dfdfdf 2px">
        <v-card
          class="text-left pa-4"
          flat
          style="width: 40%; min-width: 586px !important"
        >
          <v-card-title class="pl-0">Colour Schemes</v-card-title>
          <!--   map  colour scheme   -->
          <div>
            <v-card-subtitle class="pl-0 pb-1">
              The colours currently used for map
              visualisations.</v-card-subtitle
            >
            <div>
              <div
                v-for="colour in allColourSchemes
                  .filter(
                    (colourScheme) =>
                      colourScheme.id === clientObject.map_colour_scheme_id,
                  )[0]
                  .scheme.split('-')"
                :key="colour.id"
                class="colour-block"
              >
                <div
                  style=""
                  class="inner-block"
                  :style="{ backgroundColor: '#' + colour }"
                ></div>
              </div>

              <v-tooltip text="Change the colours used for map visualisations">
                <template v-slot:activator="{ props }">
                  <v-icon
                    v-bind="props"
                    aria-label="edit"
                    style="cursor: pointer"
                    @click="selectColourScheme(1)"
                    @keydown.enter="selectColourScheme(1)"
                    class="mb-5 ml-2"
                    tabindex="0"
                    >mdi-pencil
                  </v-icon>
                </template>
              </v-tooltip>
            </div>
          </div>
          <!--   dashboard colour scheme   -->
          <div>
            <v-card-subtitle class="pl-0 pb-1">
              The current dashboard colour scheme.</v-card-subtitle
            >
            <div
              v-for="colour in allColourSchemes
                .filter(
                  (colourScheme) =>
                    colourScheme.id === clientObject.dashboard_colour_scheme_id,
                )[0]
                .scheme.split('-')"
              style=""
              :key="colour.id"
              class="colour-block"
            >
              <div
                style=""
                class="inner-block"
                :style="{ backgroundColor: '#' + colour }"
              ></div>
            </div>
            <v-tooltip text="Change the dashboard colour scheme">
              <template v-slot:activator="{ props }">
                <v-icon
                  v-bind="props"
                  aria-label="edit"
                  style="cursor: pointer"
                  @click="selectColourScheme(2)"
                  @keydown.enter="selectColourScheme(2)"
                  class="mb-5 ml-2"
                  tabindex="0"
                  >mdi-pencil
                </v-icon>
              </template>
            </v-tooltip>
          </div>
          <v-card-title class="pl-0">Map Settings</v-card-title>
          <v-card-text class="pl-0">
            <v-card-subtitle class="pl-0 pb-1">
              Set your optional map settings, such as zoom, center, and boundary
            </v-card-subtitle>
            <DefaultZoomLevel></DefaultZoomLevel>
            <v-card-subtitle class="pl-0 py-2">
              Select the default indicator to show on the map
            </v-card-subtitle>
            <div class="d-flex gap-3 mb-3">
              <v-btn
                tile
                color="primary"
                variant="elevated"
                @click="mapDataDialog = true"
                aria-label="set default indicator"
                append-icon="mdi-chart-line"
                width="270px"
              >
                Set Default Indicator
              </v-btn>
              <v-btn
                tile
                color="error"
                variant="elevated"
                @click="removeDefaultIndicator"
                aria-label="remove default indicator"
                append-icon="mdi-delete"
                width="290px"
                class="ml-4"
                :disabled="!selectedDefaultMapIndicator"
              >
                Remove Default Indicator
              </v-btn>
            </div>

            <!-- Display current selection -->
            <section
              v-if="selectedIndicatorName"
              class="ml-7 border rounded pa-2 mb-3"
            >
              <v-label> Currently selected default indicator </v-label>
              <div class="d-flex align-center mt-2">
                <v-icon icon="mdi-check-circle" color="success" class="mr-2" />
                <span class="text-body-1">{{ selectedIndicatorName }}</span>
              </div>
            </section>

            <MapData
              v-model:mapDataDialog="mapDataDialog"
              @selectedIndicator="updateSelectedIndicator"
              v-model:selectedIndicator="selectedDefaultMapIndicator"
            />
          </v-card-text>
          <v-card-title class="pl-0">Report Settings</v-card-title>
          <v-card-text class="pl-0">
            <v-card-subtitle class="pl-0 pb-0">
              Include Appendix in Word/DocX report Downloads
            </v-card-subtitle>
            <v-checkbox
              v-model="clientObject.add_appendix_to_report_doc"
              class="ma-0 pa-0"
              density="compact"
              label="When enabled, appendix will be included in downloaded reports"
              hide-details="auto"
              color="info"
              @update:modelValue="saveSettings"
            ></v-checkbox>
          </v-card-text>
          <v-card-title class="pl-0">Authentication Settings</v-card-title>
          <v-card-text class="pl-0">
            <v-card-subtitle class="pl-0 pb-1">
              Two Factor Authentication
            </v-card-subtitle>
            <v-alert type="info"
              >These settings control whether <b>all</b> users have to provide
              2FA while logging into Local Insight, and how often they have to
              re-validate. Users will receive a one time password (OTP) via
              email that they have to enter at the login screen.</v-alert
            >
            <!-- enable/disable 2fa-->
            <v-checkbox
              v-model="clientObject.twofa_enabled"
              class="ma-0 pa-0"
              density="compact"
              label="Enable two factor authentication (2FA) for all users"
              hide-details="auto"
              color="info"
              @update:modelValue="saveSettings"
            ></v-checkbox>
            <!-- 2fa verified validity period -->
            <section class="ml-7 border rounded pa-2">
              <v-label for="twofa-valid-days">
                Number of days 2FA is valid before user is prompted again
              </v-label>
              <v-number-input
                v-model="clientObject.twofa_valid_days"
                control-variant="stacked"
                :min="0"
                :max="999"
                id="twofa-valid-days"
                style="width: 7em"
                @update:modelValue="saveSettings"
              ></v-number-input>
            </section>
          </v-card-text>
        </v-card>
        <!--         <v-card style="border-top: solid #dfdfdf 2px">
          <v-card-actions>
            <v-btn
              color="success"
              tile
              title="Save changes"
              @click="saveSettings"
              variant="elevated"
              >save
            </v-btn>
          </v-card-actions>
        </v-card> -->
      </v-card>
    </v-card>

    <v-dialog v-model="colorSchemeDialog" width="400" persistent>
      <v-card>
        <v-card-title
          :style="
            'background-color: ' +
            this.$store.state.config.siteConfig.toolbar_colour
          "
          class="text-h6 text-center"
        >
          <v-spacer>Color scheme for {{ selectedColourScheme.key }}</v-spacer>
        </v-card-title>
        <v-card-text
          class="selectColorScheme"
          style="height: 52vh; overflow: auto"
        >
          <v-radio-group v-model="selectedColourScheme.id" class="mb-4">
            <div
              v-for="colourScheme in allColourSchemes"
              :key="colourScheme.id"
            >
              <v-radio
                :value="colourScheme.id"
                :key="colourScheme.id"
                style="display: flex; justify-content: center"
                tabindex="-1"
              >
                <template v-slot:label>
                  <v-card-actions
                    tabindex="0"
                    @keydown.enter="$event.target.click()"
                    class="pb-0"
                  >
                    <div
                      v-for="(colour, index) in colourScheme.scheme.split('-')"
                      :key="index"
                      class="colour-block"
                    >
                      <div
                        class="inner-block"
                        :style="{
                          backgroundColor: '#' + colour,
                        }"
                      ></div>
                    </div>
                  </v-card-actions>
                </template>
              </v-radio>
            </div>
          </v-radio-group>
        </v-card-text>
        <v-card>
          <v-card-actions>
            <v-btn
              variant="elevated"
              color="error"
              tile
              @click="selectColourScheme(null)"
              aria-label="cancel"
            >
              cancel
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              variant="elevated"
              color="success"
              tile
              @click="selectColourScheme(3)"
              aria-label="confirm"
            >
              confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import { useDisplay } from "vuetify";
import DefaultZoomLevel from "@/components/DefaultZoomLevel.vue";
import MapData from "@/components/MapData";

export default {
  name: "GroupSettings",
  data: () => ({
    height: useDisplay().height,
    clientObject: null,
    allColourSchemes: [],
    colorSchemeDialog: false,
    selectedColourScheme: {
      id: null,
      key: null,
    },
    showAreaSelector: false,
    defaultMapZoomDialog: false,
    mapDataDialog: false,
    selectedDefaultMapIndicator: null,
  }),
  mounted() {
    this.getSettings();
    this.getColourSchemes();
  },
  computed: {
    loading() {
      return !this.clientObject || this.allColourSchemes.length === 0;
    },
    selectedIndicatorName() {
      // Return the indicator name if we have the client object and a selected indicator
      if (this.clientObject && this.clientObject.default_map_indicator_name) {
        return this.clientObject.default_map_indicator_name;
      }
      return null;
    },
  },
  components: {
    DefaultZoomLevel,
    MapData,
  },
  methods: {
    getSettings() {
      this.$axios
        .get("/client-settings")
        .then(
          function (response) {
            // handle success
            this.clientObject = response.data;
            // Populate selectedDefaultMapIndicator with existing setting
            this.selectedDefaultMapIndicator =
              this.clientObject.default_map_indicator_id || null;
            this.loading = false;
          }.bind(this),
        )
        .catch(
          function (error) {
            // handle error
            console.error(error);
            this.emit.emit("systemMessage", {
              title: "Error! Failed to get client settings",
              message: error.response.data.message,
              timeout: 0,
              colour: "error",
            });
          }.bind(this),
        );
    },
    getColourSchemes() {
      this.$axios
        .get("/all-colour-schemes")
        .then(
          function (response) {
            // handle success
            this.allColourSchemes = response.data;
          }.bind(this),
        )
        .catch(
          function (error) {
            // handle error
            console.error(error);
            this.emit.emit("systemMessage", {
              message: error.response.data.message,
              title: "Error! Failed to get colour schemes",
              timeout: -1,
              colour: "error",
            });
          }.bind(this),
        );
    },
    saveSettings() {
      this.emit.emit("systemBusy", true);
      this.$axios
        .post("/save-settings", this.clientObject)
        .then(
          function (response) {
            // handle success
            this.clientObject = response.data;
            this.$store.state.config.customClientConfig.add_appendix_to_report_doc =
              this.clientObject.add_appendix_to_report_doc;
            this.emit.emit("systemMessage", {
              title: "Settings saved successfully!",
              message: "",
              timeout: 3000,
              colour: "success",
            });
            this.emit.emit("systemBusy", false);
          }.bind(this),
        )
        .catch(
          function (error) {
            // handle error
            console.error(error);
            this.emit.emit("systemBusy", false);
            this.emit.emit("systemMessage", {
              message: error.response.data.message,
              title: "Error! Failed to save the settings",
              timeout: 0,
              colour: "error",
            });
          }.bind(this),
        );
    },
    selectColourScheme(item) {
      // grab all colour schemes if we haven't already
      if (!this.allColourSchemes.length) this.getColourSchemes();

      switch (item) {
        case 1:
          // map colour scheme
          this.selectedColourScheme = {
            key: "Map",
            id: this.clientObject.map_colour_scheme_id,
          };
          this.colorSchemeDialog = true;
          break;
        case 2:
          // dashboard colour scheme
          this.selectedColourScheme = {
            key: "Dashboard",
            id: this.clientObject.dashboard_colour_scheme_id,
          };
          this.colorSchemeDialog = true;
          break;
        case 3:
          // confirm colour scheme
          if (this.selectedColourScheme.key === "Map") {
            this.clientObject.map_colour_scheme_id =
              this.selectedColourScheme.id;
          } else {
            this.clientObject.dashboard_colour_scheme_id =
              this.selectedColourScheme.id;
          }
          this.saveSettings();
          this.colorSchemeDialog = false;
          break;
        default:
          // cancel colour scheme
          this.colorSchemeDialog = false;
      }
    },
    updateSelectedIndicator(indicator) {
      this.selectedDefaultMapIndicator = indicator;
      this.clientObject.default_map_indicator_id = indicator;
      this.saveSettings();
    },
    removeDefaultIndicator() {
      this.selectedDefaultMapIndicator = null;
      this.clientObject.default_map_indicator_id = null;
      this.saveSettings();
    },
  },
  watch: {},
};
</script>
<style scoped>
.colour-block {
  display: inline-block;
  margin-top: 5px;
}

.inner-block {
  width: 50px;
  height: 25px;
}
</style>
<style>
.selectColorScheme .v-label {
  opacity: 1 !important;
}
</style>
